import axios from "axios";
import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import html2pdf from "html2pdf.js";
import { PDFDocument } from "pdf-lib";
import { useNavigate } from "react-router-dom";
import { getSpecialty } from "../../../../../utils/specialty";

const { REACT_APP_API_KEY } = process.env || {};

const useSavePrescription = ({ method = "post", contentRef }) => {
  const navigate = useNavigate();

  const [isPrescriptionSaving, setIsPrescriptionSaving] = useState(false);
  const prescriptionData = useSelector((state) => state.prescriptionData);
  const [pastPrescription, setPastPrescription] = useState([]);
  const userDetails = JSON.parse(sessionStorage.getItem("userDetails"));

  const { signature, stamp } = userDetails || {};

  const patientData = useSelector((state) => state.patientDetails);
  const { name } = patientData || {};

  const {
    chiefComplaint = [],
    diagnosis = [],
    rxList = [],
    labInvestigations = [],
    medicalHandoubts = [],
    instructions,
    lifeStyleRecommendations,
    followUpDate,
    // followUp,
  } = prescriptionData || {};

  const getAllHandoubts = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      // const res = await axios.get(
      //   `${REACT_APP_API_KEY}/api/v1/mmu/cortex/handout`,

      //   {
      //     params: { id: medicalHandoubts[0]?.id },
      //     headers: {
      //       Authorization: `Bearer ${token}`,
      //       "ngrok-skip-browser-warning": "hgs",
      //     },
      //     responseType: "blob",
      //   }
      // );
      // const pdfUrl = URL.createObjectURL(res.data);
      const pdfUrls = await Promise.all(
        medicalHandoubts.map(async (handout) => {
          const res = await axios.get(
            `/api/v1/cortex/handout`,

            {
              params: { id: handout?.id, speciality: getSpecialty() },
              headers: {
                Authorization: `Bearer ${token}`,
                "ngrok-skip-browser-warning": "hgs",
              },
              responseType: "blob",
            }
          );
          return URL.createObjectURL(res.data);
        })
      );
      return pdfUrls;
    } catch (error) {
      console.error(error, "error");
      const errorMessage = error.response?.data?.message;
      if (!error.response) {
        toast.error("Oops, something went wrong");
      } else {
        const messageContent = Array.isArray(errorMessage) ? (
          <ul className="ms-2 list-disc">
            {errorMessage.map((msg, index) => (
              <li key={index}>{msg}</li>
            ))}
          </ul>
        ) : (
          errorMessage || "An unknown error occurred"
        );
        toast.error(messageContent);
        if (error.response?.data?.statusCode === 401) {
          localStorage.removeItem("authToken");
          setTimeout(() => {
            navigate("/");
          }, 1000);
        }
      }
    }
  }, [medicalHandoubts, navigate]);

  const onSavePrescription = useCallback(
    async (id) => {
      setIsPrescriptionSaving(true);
      const token = localStorage.getItem("authToken");
      try {
        // Generate PDF from HTML first
        const element = contentRef.current;
        const opt = {
          margin: [10, 10, 10, 10],
          filename: `${name}_prescription.pdf`,
          image: { type: "jpeg", quality: 0.5 },
          html2canvas: {
            scale: 1,
            useCORS: true,
            logging: false,
            letterRendering: false
          },
          jsPDF: {
            orientation: "p",
            unit: "mm",
            format: "a4",
            putOnlyUsedFonts: true,
            floatPrecision: 16,
            compress: true
          },
        };

        // Generate single prescription PDF
        const pdfBlob = await html2pdf().set(opt).from(element).outputPdf("blob");
        // Compress the PDF
        const compressedPdfBlob = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = async (e) => {
            const pdfData = e.target.result;
            const pdfArray = new Uint8Array(pdfData);

            const pdfDoc = await PDFDocument.load(pdfArray);
            const compressedPdfBytes = await pdfDoc.save({
              useObjectStreams: true,
              addDefaultPage: false,
              objectsPerTick: 50,
              updateMetadata: false
            });

            resolve(new Blob([compressedPdfBytes], { type: 'application/pdf' }));
          };
          reader.readAsArrayBuffer(pdfBlob);
        });

        // Convert to base64
        const prescriptionPdfBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result.split(",")[1]);
          reader.readAsDataURL(compressedPdfBlob);
        });

        // Prepare payload - only sending prescription data
        const payload = {
          chiefComplaint: chiefComplaint,
          followUp: followUpDate,
          lifeStyleRecommendations: lifeStyleRecommendations,
          instructions: instructions,
          diagnosis: diagnosis,
          labInvestigations: labInvestigations,
          rxList: rxList[0]?.drugName === "" ? [] : rxList,
          visitId: id,
          signature: signature,
          stamp: stamp,
          pdfBase64: prescriptionPdfBase64,
          handouts: medicalHandoubts.map((handout) => handout.id)
        };

        // Make API request
        const res = await axios[method](
          `/api/v1/records/prescription`,
          method === "post"
            ? payload
            : {
              params: {
                patientId: id,
              }
            },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "ngrok-skip-browser-warning": "hgs",
              'Content-Type': 'application/json',
            },
            maxContentLength: Infinity,
            maxBodyLength: Infinity,
            timeout: 30000,
          }
        );

        if (method === "post") {
          toast.success("Prescription Generated Successfully!", {
            className: "w-max",
            position: "top-center",
          });
          setPastPrescription(res.data);
          navigate(-1);
        }

        const pdfUrl = URL.createObjectURL(pdfBlob);
        const a = document.createElement('a');
        a.href = pdfUrl;
        a.download = `${name}_prescription.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        return res;
      } catch (error) {
        console.error("Error generating prescription:", error);
        toast.error(error.response?.data?.message);
        // throw error;
      } finally {
        setIsPrescriptionSaving(false);
      }
    },
    [contentRef, navigate, chiefComplaint, followUpDate, lifeStyleRecommendations,
      instructions, diagnosis, labInvestigations, rxList, signature, stamp,
      method, name]
  );

  return { onSavePrescription, isPrescriptionSaving, pastPrescription };
};

export default useSavePrescription;
